/**
 * MSG-7 (дизайн §6): выбор лидера между вкладками через Web Locks.
 * Криптоядро НЕ потокобезопасно, и две вкладки на одном IndexedDB-сторе
 * портят состояние Olm-сессий — чат работает только во вкладке-лидере,
 * остальные честно говорят «чат открыт в другой вкладке» (критерий 4).
 *
 * Лок держится до release() или закрытия вкладки (браузер снимает Web Lock
 * сам — «умерший лидер» не запирает чат навсегда).
 *
 * release() возвращает промис «лок ФАКТИЧЕСКИ отпущен» (QR-1b р.3): это
 * промис самого locks.request — он резолвится после того, как менеджер локов
 * снял удержание. Передача лидерства внутри вкладки (/msg/5 -> /msg/m/7,
 * msgChat.jsx) ждёт именно его, а не момент вызова release.
 *
 * 🔴 УСТУПКА лидерства (с.64). Отказ `ifAvailable` сам по себе — тупик на
 * телефоне: совет «работайте в той вкладке или закройте её» невыполним, вкладок
 * там не видно. 25.08 на бою из-за этого владелец не вошёл НИ В ОДНУ комнату
 * участников: соседняя вкладка держала лок на чате с уже удалённым участником,
 * а страницы новых комнат молча не делали ни одного сетевого запроса (разбор
 * `Задачи/Разбор-прогона-25.08-владелец-не-вошёл-в-комнаты.md`). Поэтому у
 * заблокированной вкладки есть второй путь: попросить держателя уступить
 * (BroadcastChannel) и встать за локом В ОЧЕРЕДЬ с таймаутом.
 *
 * 🔴 Уступка НЕ ослабляет инвариант «две машины на одном сторе недопустимы»:
 * держатель отдаёт лок тем же путём, что и при уходе со страницы — cleanup
 * эффекта останавливает контроллер (stop() дожидается очереди и закрывает
 * стор) и только ПОТОМ вызывает release. Здесь нет второго, «быстрого» пути
 * отдачи лока — есть только сигнал, по которому запускается штатный.
 */
const LOCK_NAME = 'total-msg-leader';
const YIELD_CHANNEL = 'total-msg-leader-yield';

/**
 * Сколько ждать лок в очереди после просьбы уступить. Держатель отдаёт его не
 * мгновенно: stop() дожидается уже начатого шага машины (подъём, догон,
 * отправка), а это сеть. Меньше — экран врал бы «не удалось» здоровому
 * переносу; больше — человек смотрит в спиннер и уходит.
 */
export const TAKEOVER_WAIT_MS = 10000;

/**
 * @param {{waitMs?: number}} opts  waitMs > 0 — встать за локом В ОЧЕРЕДЬ
 *   (перенос чата сюда), иначе прежнее поведение: занят — сразу отказ.
 */
export function requestLeadership({ waitMs = 0 } = {}) {
  const locks = globalThis.navigator?.locks;
  if (!locks?.request) {
    // Старый браузер без Web Locks: пускаем (одна вкладка — типовой случай),
    // риск двух вкладок принимаем осознанно — иначе чат недоступен вовсе.
    return Promise.resolve({ granted: true, release: () => Promise.resolve() });
  }
  return new Promise((resolve) => {
    let releaseHeld;
    const held = new Promise((r) => { releaseHeld = r; });
    const noRelease = () => Promise.resolve();
    // Ждать перестали (таймаут), а менеджер лок всё-таки выдал: держать его
    // НЕЛЬЗЯ — вкладка уже показала отказ и чат не поднимает, а лок остался бы
    // за ней навсегда и запер бы чат во всём браузере.
    let gaveUp = false;
    let timer = null;
    const stopTimer = () => { if (timer) { clearTimeout(timer); timer = null; } };
    let opts = { ifAvailable: true };
    if (waitMs > 0) {
      // signal — штатный способ снять запрос из очереди. Своим таймером
      // страхуемся отдельно: движок без поддержки signal молча проигнорирует
      // опцию и оставит нас ждать вечно.
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      opts = ctrl ? { signal: ctrl.signal } : {};
      timer = setTimeout(() => {
        gaveUp = true;
        timer = null;
        try { ctrl?.abort(); } catch { /* уже выдан/отменён — ниже отказ */ }
        resolve({ granted: false, release: noRelease });
      }, waitMs);
    }
    const requested = locks
      .request(LOCK_NAME, opts, (lock) => {
        stopTimer();
        if (!lock) {
          resolve({ granted: false, release: noRelease });
          return undefined; // лок не наш — держать нечего
        }
        if (gaveUp) return undefined; // опоздал: отпускаем сразу, не запирая чат
        resolve({
          granted: true,
          // отпустить И дождаться снятия удержания менеджером локов
          release: () => { releaseHeld(); return released; },
        });
        return held; // лок держится, пока страница не отпустит
      });
    const released = requested.then(() => undefined, () => undefined);
    requested.catch(() => {
      stopTimer();
      resolve({ granted: false, release: noRelease });
    });
  });
}

/**
 * Канал уступки — ОДИН на документ: BroadcastChannel не доставляет сообщение
 * собственному объекту-каналу, поэтому вкладка, попросившая уступить, не
 * услышит саму себя, а её сосед — услышит.
 */
let channel = null;
function getChannel() {
  const BC = globalThis.BroadcastChannel;
  if (!BC) return null; // нет канала — уступка недоступна, экран скажет честно
  if (!channel) channel = new BC(YIELD_CHANNEL);
  return channel;
}

/** Есть ли вообще чем просить уступку (для честного экрана без кнопки). */
export const canAskYield = () => Boolean(globalThis.BroadcastChannel);

/** Попросить держателя отдать лидерство. Доставка не гарантируется — ответ
 *  виден только по тому, достался ли лок в очереди. */
export function askYield() {
  const ch = getChannel();
  if (!ch) return;
  try { ch.postMessage({ type: 'yield' }); } catch { /* канал закрыт — таймаут скажет */ }
}

/** Подписка держателя на просьбу уступить. Возвращает отписку. */
export function onYieldRequest(handler) {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (e) => { if (e?.data?.type === 'yield') handler(); };
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}

/** Для тестов: закрыть и забыть канал документа. */
export function resetYieldChannel() {
  try { channel?.close(); } catch { /* уже закрыт */ }
  channel = null;
}

/**
 * Передача лидерства ВНУТРИ вкладки между СТРАНИЦАМИ msg-машины.
 *
 * 🔴 Почему здесь, а не в странице чата (QR-2 В2). Механизм завёл `msgChat`
 * (ревью QR-1b р.3, High): при переходе /msg/5 -> /msg/m/7 прежний контроллер
 * отпускает Web Lock только ПОСЛЕ своего `stop()`, а новый mount просил бы лок
 * сразу с `ifAvailable` — и получал бы честный отказ от СОБСТВЕННОГО, ещё не
 * отпущенного лока, то есть «чат открыт в другой вкладке» навсегда.
 * Пока страница была одна, модульная переменная внутри неё это решала. С
 * приходом экрана комнаты страниц стало ДВЕ, у каждого модуля своя переменная
 * — и переход «личный чат -> комната» в той же вкладке вернул бы ровно тот
 * баг. Замок один на origin (`total-msg-leader`), значит и эстафета обязана
 * быть одна.
 *
 * Другая вкладка — другой контекст, её это не касается: для неё отказ по
 * `ifAvailable` остаётся честным и мгновенным.
 */
let localHandoff = Promise.resolve();

/** Дождаться, пока прежняя страница этой вкладки остановилась и отпустила лок. */
export const waitLocalHandoff = () => localHandoff;

/** Положить эстафету: cleanup страницы отдаёт сюда свой «остановлен и отпущен». */
export const setLocalHandoff = (promise) => { localHandoff = promise; };
